import { useParams } from 'react-router-dom';

import Button from 'components/button';
import { useDispatch, useSelector } from 'store/hooks';
import ToggleBox from 'components/toggle-box';
import { useOCR } from 'layouts/application';
import { toast } from 'react-toastify';
import ResultBox from 'components/result-box';
import classNames from 'classnames';
import React from 'react';
import styles from './scoring.module.scss';
import { stopFactorStatusTranscript } from 'utils/prettier-status';

const LegalCase = () => {
	const { id } = useParams();
	const contextApplication = useSelector((state) => state.application);
	const dispatch = useDispatch();
	const [pending, setPending] = React.useState(false);
	const [formValid, setFormValid] = React.useState(false);
	const {
		handleRunLegalCase,
		hasLegalCase,
		setHasLegalCase,
		legalCaseHasCorrectType,
		setLegalCaseHasCorrectType,
		guarantorsHasLegalCase,
		guarantorsLegalCaseCorrectType,
		setGuarantorsHasLegalCase,
		setGuarantorsLegalCaseCorrectType,
	} = useOCR();

	React.useEffect(() => {
		setFormValid(
			!contextApplication.data?.legal_case_log &&
				contextApplication.data?.guarantor_legal_case_log.length === 0 &&
				(!hasLegalCase || legalCaseHasCorrectType !== null) &&
				!guarantorsHasLegalCase.find(
					(gHasLC, index) => gHasLC === true && guarantorsLegalCaseCorrectType[index] === null,
				),
		);
	}, [
		contextApplication.data?.guarantor_legal_case_log,
		contextApplication.data?.legal_case_log,
		guarantorsHasLegalCase,
		guarantorsLegalCaseCorrectType,
		hasLegalCase,
		legalCaseHasCorrectType,
	]);

	const handleLegalCase = async () => {
		try {
			setPending(true);
			await handleRunLegalCase();
			setTimeout(() => {
				dispatch.application.FETCH_APPLICATION_BY_ID({ id });
			}, 1000);
		} catch (error: any) {
			toast.error(JSON.stringify(error.response?.data?.detail?.[0]));
		} finally {
			setPending(false);
			setFormValid(false);
		}
	};

	return (
		<div className={styles.home}>
			<div className={styles.fields}>
				{(contextApplication.data?.legal_case_log ||
					contextApplication.data?.guarantor_legal_case_log.length !== 0) && (
					<div className={styles.creditDataBox}>
						<div className={styles.title}>Legal case</div>
						<div className={styles.table}>
							{contextApplication.data?.legal_case_log && (
								<div className={styles.row}>
									<div className={styles.col}>Risky legal case</div>
									<div className={styles.col}>
										{contextApplication.data.legal_case_log?.legal_case_has_correct_type ?? 'N/A'}
									</div>
									<div
										className={classNames(
											styles.col,
											(contextApplication.data.legal_case_log?.is_approved === null ||
												contextApplication.data.legal_case_log?.is_approved === 'approved') &&
												styles.green,
											contextApplication.data.legal_case_log?.is_approved === 'rejected' &&
												styles.red,
											contextApplication.data.legal_case_log?.is_approved === 'review' &&
												styles.yellow,
										)}
									>
										{stopFactorStatusTranscript(
											contextApplication.data.legal_case_log?.is_approved,
										)}
									</div>
								</div>
							)}
							{contextApplication.data?.guarantor_legal_case_log.length !== 0 &&
								contextApplication.data?.guarantor_legal_case_log.map((lc, index) => (
									<div className={styles.row} key={lc.id}>
										<div className={styles.col}>Guarantor risky legal case {index + 1}</div>
										<div className={styles.col}>{lc?.legal_case_has_correct_type ?? 'N/A'}</div>
										<div
											className={classNames(
												styles.col,
												(lc?.is_approved === null || lc?.is_approved === 'approved') &&
													styles.green,
												lc?.is_approved === 'rejected' && styles.red,
												lc?.is_approved === 'review' && styles.yellow,
											)}
										>
											{stopFactorStatusTranscript(lc?.is_approved)}
										</div>
									</div>
								))}
						</div>
					</div>
				)}
				{!contextApplication.data?.legal_case_log &&
					contextApplication.data?.guarantor_legal_case_log.length === 0 && (
						<>
							<div className={styles.fieldGroup}>
								<div className={styles.row}>
									<label className={styles.toggleBoxInput}>
										<ToggleBox
											active={hasLegalCase}
											onToggle={() => {
												setLegalCaseHasCorrectType(null);
												setHasLegalCase(!hasLegalCase);
											}}
										/>
										Client has legal case
									</label>
								</div>
								{hasLegalCase && (
									<div className={styles.legal_case_input}>
										<div className={styles.legal_case_title}>
											Is the legal case one of the following types:
										</div>
										<div className={styles.legal_case_list}>
											<div className={styles.legal_case_variant}>Financial issue</div>
											<div className={styles.legal_case_variant}>Bad credit reputation</div>
											<div className={styles.legal_case_variant}>
												Development and Operation Warning
											</div>
											<div className={styles.legal_case_variant}>Removal of commons</div>
											<div className={styles.legal_case_variant}>Public auction</div>
										</div>
										<div className={styles.legal_case_selection}>
											<label className={styles.legal_case_radio}>
												<input
													type="radio"
													name="legal_case_type"
													checked={legalCaseHasCorrectType === 'Yes'}
													onChange={() => setLegalCaseHasCorrectType('Yes')}
												/>
												Yes
											</label>
											<label className={styles.legal_case_radio}>
												<input
													type="radio"
													name="legal_case_type"
													checked={legalCaseHasCorrectType === 'No'}
													onChange={() => setLegalCaseHasCorrectType('No')}
												/>
												No
											</label>
										</div>
									</div>
								)}
							</div>
							{guarantorsHasLegalCase.map((ghlc, index) => (
								<div className={styles.fieldGroup} key={index}>
									<div className={styles.row}>
										<label className={styles.toggleBoxInput}>
											<ToggleBox
												active={ghlc}
												onToggle={() => {
													const newGuarantorsLegalCaseCorrectType = [
														...guarantorsLegalCaseCorrectType,
													];
													newGuarantorsLegalCaseCorrectType[index] = null;
													setGuarantorsLegalCaseCorrectType(newGuarantorsLegalCaseCorrectType);
													setGuarantorsHasLegalCase(
														guarantorsHasLegalCase.map((ghlc, i) => (index === i ? !ghlc : ghlc)),
													);
												}}
											/>
											Guarantor {index + 1} has legal case
										</label>
									</div>
									{ghlc && (
										<div className={styles.legal_case_input}>
											<div className={styles.legal_case_title}>
												Is the legal case one of the following types:
											</div>
											<div className={styles.legal_case_list}>
												<div className={styles.legal_case_variant}>Financial issue</div>
												<div className={styles.legal_case_variant}>Bad credit reputation</div>
												<div className={styles.legal_case_variant}>
													Development and Operation Warning
												</div>
												<div className={styles.legal_case_variant}>Removal of commons</div>
												<div className={styles.legal_case_variant}>Public auction</div>
											</div>
											<div className={styles.legal_case_selection}>
												<label className={styles.legal_case_radio}>
													<input
														type="radio"
														name={'guarantor_legal_case_type_' + index}
														checked={guarantorsLegalCaseCorrectType[index] === 'Yes'}
														onChange={() => {
															setGuarantorsLegalCaseCorrectType(
																guarantorsLegalCaseCorrectType.map((glct, i) =>
																	index === i ? 'Yes' : glct,
																),
															);
														}}
													/>
													Yes
												</label>
												<label className={styles.legal_case_radio}>
													<input
														type="radio"
														name={'guarantor_legal_case_type_' + index}
														checked={guarantorsLegalCaseCorrectType[index] === 'No'}
														onChange={() => {
															setGuarantorsLegalCaseCorrectType(
																guarantorsLegalCaseCorrectType.map((glct, i) =>
																	index === i ? 'No' : glct,
																),
															);
														}}
													/>
													No
												</label>
											</div>
										</div>
									)}
								</div>
							))}
						</>
					)}
				<div className={styles.footerBox}>
					<div className={styles.buttonGroup}>
						{formValid && (
							<Button pending={pending} onClick={handleLegalCase}>
								Calculate
							</Button>
						)}
					</div>
				</div>
			</div>
			<div className={styles.result}>
				<ResultBox />
			</div>
		</div>
	);
};

export default LegalCase;
